import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
  CustomizerCartPendingStartEvent,
  CustomizerCartPendingEndEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/** Abort cart/add if artwork upload hangs (common with multiple huge files). */
const CART_ADD_TIMEOUT_MS = 90000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));

    if (productForm && typeof productForm.checkQuantityLimit === 'function') {
      const validation = productForm.checkQuantityLimit();
      if (!validation.canAdd) {
        event.preventDefault();
        event.stopPropagation();
        productForm.handleQuantityLimitFailure(validation);
        return;
      }
    } else if (productForm?.refs.quantitySelector?.canAddToCart) {
      const validation = productForm.refs.quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }

    if (!form?.checkValidity()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} addToCartTextErrorMessage - The add to cart error message text.
 * @property {HTMLButtonElement | undefined} addToCartTextErrorClose - The add to cart error close button.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest('.product-form-text__error-close')) return;
        event.preventDefault();
        this.#hideAddToCartError();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * @param {string | undefined} variantId
   * @param {number} quantityAdded
   */
  #optimisticUpdateCartQuantity(variantId, quantityAdded) {
    if (!variantId || quantityAdded <= 0) return;

    const quantitySelector = /** @type {{ setCartQuantity?: (qty: number) => void } | null} */ (
      this.querySelector('quantity-selector-component, cart-quantity-selector-component')
    );
    const quantityInput = this.querySelector('input[data-cart-quantity]');
    const currentQty = quantityInput
      ? parseInt(quantityInput.getAttribute('data-cart-quantity') || '0', 10)
      : 0;
    const nextQty = currentQty + quantityAdded;

    quantitySelector?.setCartQuantity?.(nextQty);
    this.#updateQuantityLabel(nextQty);
  }

  /**
   * @param {Record<string, string> | undefined} sections
   * @param {string[]} sectionIds
   * @returns {number | null}
   */
  #getItemCountFromSections(sections, sectionIds) {
    if (!sections) return null;

    for (const sectionId of sectionIds) {
      const html = sections[sectionId];
      if (!html) continue;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const countText = doc.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
      if (!countText) continue;

      const count = parseInt(countText, 10);
      if (Number.isFinite(count)) return count;
    }

    return null;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    const customizer = this.#getProductCustomizer();

    if (customizer?.requiresNativeSubmit()) {
      void this.#processCustomizerAddToCart(customizer, event);
      return;
    }

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    void this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * Public bulk-order entry point used by the bulk order matrix component.
   * @param {Array<{variantId: string, quantity: number, colorSlug?: string, featuredImageUrl?: string}>} items
   * @param {Event} [event]
   * @returns {Promise<boolean>}
   */
  async submitBulkOrder(items, event) {
    const normalizedItems = items
      .filter((item) => item?.variantId && Number(item.quantity) > 0)
      .map((item) => ({
        variantId: String(item.variantId),
        quantity: Number(item.quantity),
        colorSlug: item.colorSlug || '',
        featuredImageUrl: item.featuredImageUrl || '',
      }));
    if (!normalizedItems.length) return false;

    const customizer = this.#getProductCustomizer();
    if (customizer?.hasActiveDesigns?.()) {
      return await this.#processBulkCustomizerAddToCart(customizer, normalizedItems, event);
    }

    return await this.#processBatchAddToCart(normalizedItems, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @returns {string[]}
   */
  #getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    return sectionIds;
  }

  /**
   * @returns {Promise<Cart | null>}
   */
  async #fetchCartSnapshot() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart snapshot:', error);
      return null;
    }
  }

  /**
   * @param {Cart | null} cart
   * @param {string} customizerKey
   * @param {string} anchorVariantId
   * @returns {{ variant_id?: number, properties?: Record<string, string> } | null}
   */
  #findCustomizerAnchorLine(cart, customizerKey, anchorVariantId) {
    if (!cart?.items?.length) return null;

    const exactMatch = cart.items.find((item) => {
      return (
        item?.properties?.['_Customizer Key'] === customizerKey &&
        item?.properties?.['_Customizer Add-on'] !== 'true' &&
        String(item.variant_id) === String(anchorVariantId)
      );
    });

    if (exactMatch) return exactMatch;

    return (
      cart.items.find((item) => {
        return (
          item?.properties?.['_Customizer Key'] === customizerKey &&
          item?.properties?.['_Customizer Add-on'] !== 'true'
        );
      }) || null
    );
  }

  #redirectToCartPage() {
    document.documentElement.dataset.customizerCartRedirecting = 'true';
    const cartUrl = Theme?.routes?.cart_url || '/cart';
    window.location.assign(cartUrl);
  }

  /**
   * @param {Record<string, unknown> | undefined} properties
   * @returns {Record<string, string>}
   */
  #extractArtworkPropertyUrls(properties) {
    /** @type {Record<string, string>} */
    const urls = {};

    if (!properties) return urls;

    for (const [key, value] of Object.entries(properties)) {
      if (typeof value === 'string' && value && key.includes(' Artwork')) {
        urls[key] = value;
      }
    }

    return urls;
  }

  /**
   * @param {Record<string, unknown> | undefined} properties
   * @returns {Record<string, string>}
   */
  #copyLineItemProperties(properties) {
    /** @type {Record<string, string>} */
    const copied = {};

    if (!properties) return copied;

    for (const [key, value] of Object.entries(properties)) {
      if (key === '_Customizer Add-on') continue;
      if (typeof value !== 'string' || value === '') continue;
      copied[key] = value;
    }

    return copied;
  }

  /**
   * @returns {{ canAdd: boolean, maxQuantity: number | null, cartQuantity?: number, quantityToAdd?: number }}
   */
  #validateQuantityLimit() {
    if (this.refs.quantitySelector?.canAddToCart) {
      return this.refs.quantitySelector.canAddToCart();
    }

    return { canAdd: true, maxQuantity: null };
  }

  async checkQuantityLimitAsync() {
    return this.#validateQuantityLimit();
  }

  /**
   * @param {{ canAdd: boolean, maxQuantity: number | null }} validation
   */
  #handleQuantityLimitFailure(validation) {
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    for (const container of allAddToCartContainers) {
      container.disable();
    }

    this.#showAddToCartError(this.#getQuantityLimitErrorMessage(validation));

    setTimeout(() => {
      for (const container of allAddToCartContainers) {
        container.enable();
      }
    }, ERROR_BUTTON_REENABLE_DELAY);
  }

  /**
   * @returns {{ canAdd: boolean, maxQuantity: number | null, cartQuantity?: number, quantityToAdd?: number }}
   */
  checkQuantityLimit() {
    return this.#validateQuantityLimit();
  }

  /**
   * @param {{ canAdd: boolean, maxQuantity: number | null }} validation
   */
  handleQuantityLimitFailure(validation) {
    this.#handleQuantityLimitFailure(validation);
  }

  /**
   * @param {{ canAdd: boolean, maxQuantity: number | null }} validation
   * @returns {string}
   */
  #getQuantityLimitErrorMessage(validation) {
    const errorTemplate = this.dataset.quantityErrorMax || '';
    return errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
  }

  /**
   * @param {string} message
   */
  #showAddToCartError(message) {
    const addToCartTextError =
      this.refs.addToCartTextError ?? this.querySelector('[ref="addToCartTextError"], .product-form-text__error');
    const addToCartTextErrorMessage =
      this.refs.addToCartTextErrorMessage ??
      this.querySelector('[ref="addToCartTextErrorMessage"], .product-form-text__error-message');

    if (!addToCartTextError) return;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = undefined;
    }

    addToCartTextError.classList.remove('hidden');

    if (addToCartTextErrorMessage) {
      addToCartTextErrorMessage.textContent = message;
    }

    addToCartTextError.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    this.#setLiveRegionText(message);

    this.#timeout = setTimeout(() => {
      this.#hideAddToCartError();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #hideAddToCartError() {
    const { addToCartTextError, addToCartTextErrorMessage } = this.refs;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = undefined;
    }

    if (addToCartTextError) {
      addToCartTextError.classList.add('hidden');
      addToCartTextError.removeAttribute('aria-live');
    }

    if (addToCartTextErrorMessage) {
      addToCartTextErrorMessage.textContent = '';
    }

    this.#clearLiveRegionText();
  }

  /**
   * @param {import('./product-customizer').ProductCustomizerComponent} customizer
   * @param {Event} event
   */
  async #processCustomizerAddToCart(customizer, event) {
    if (!customizer.validate()) return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    const validation = await this.checkQuantityLimitAsync();
    if (!validation.canAdd) {
      this.#handleQuantityLimitFailure(validation);
      return;
    }

    allAddToCartContainers.forEach((container) => {
      container.disable();
    });
    document.dispatchEvent(new CustomizerCartPendingStartEvent());

    try {
      const quantity = this.#getQuantity();
      const customizerKey = customizer.getCustomizerKey();
      const willAddAddons = customizer.hasAddOnVariants() && customizer.getEnabledLocations().length > 0;

      await customizer.syncFormFields();

      await customizer.prepareArtworkFilesForCart();

      const missingPreviews = await customizer.ensurePreviewFilesReady();
      if (missingPreviews.length) {
        const labels = missingPreviews
          .map((location) => location.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()))
          .join(', ');
        alert(`Could not generate the design preview for ${labels}. Please try again before adding to cart.`);
        return;
      }

      customizer.setCustomizerKeyOnForm(customizerKey);
      customizer.setCustomizerBundleOnForm(willAddAddons);

      const formData = new FormData(form);
      customizer.appendPropertyFilesToFormData(formData, { includePreviews: true });
      formData.set('quantity', quantity.toString());

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      /** @type {string[]} */
      const cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });

      const sectionsParam = cartItemComponentsSectionIds.join(',');
      if (!willAddAddons && sectionsParam) {
        formData.append('sections', sectionsParam);
      }

      const parsedResponse = await this.#fetchCartAddJson(formData);

      if (parsedResponse.status) {
        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', parsedResponse.message, parsedResponse.description, parsedResponse.errors)
        );

        this.#showAddToCartError(parsedResponse.message);

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: quantity,
            productId: this.dataset.productId,
          })
        );
        return;
      }

      let sections = parsedResponse.sections;

      if (willAddAddons) {
        const addonItems = customizer.getAddOnCartItems(quantity, customizerKey);

        const addonResponse = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: addonItems,
            sections: sectionsParam,
          }),
        });
        const addonParsed = await addonResponse.json();

        if (addonParsed.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, addonParsed.message, addonParsed.description, addonParsed.errors)
          );

          this.#showAddToCartError(addonParsed.message);
          return;
        }

        sections = addonParsed.sections || sections;
        this.#redirectToCartPage();
        return;
      }

      this.#hideAddToCartError();

      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
        this.#setLiveRegionText(addedText);
        setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      allAddToCartContainers.forEach((container) => {
        container.animateAddToCart?.();
      });

      const variantId = formData.get('id')?.toString();
      this.#optimisticUpdateCartQuantity(variantId, quantity);

      const itemCount = this.#getItemCountFromSections(sections, cartItemComponentsSectionIds);
      const cartResource = itemCount != null ? { item_count: itemCount } : undefined;

      this.dispatchEvent(
        new CartAddEvent(cartResource, variantId || this.id, {
          source: 'product-form-component',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections,
        })
      );
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Could not add your customised product to the cart. Please try again.';
      this.#showAddToCartError(message);
      alert(message);
    } finally {
      document.dispatchEvent(new CustomizerCartPendingEndEvent());
      allAddToCartContainers.forEach((container) => {
        container.enable();
      });

      if (event) {
        cartPerformance.measureFromEvent('add:user-action', event);
      }
    }
  }

  /**
   * @param {FormData} formData
   * @returns {Promise<any>}
   */
  async #fetchCartAddJson(formData) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), CART_ADD_TIMEOUT_MS);

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        signal: controller.signal,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          'Could not add your customised product to the cart. The artwork files may be too large — please try again.'
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error(
          'Adding to cart timed out while uploading your artwork. Try again, or use slightly smaller image files.'
        );
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  /**
   * @returns {import('./product-customizer').ProductCustomizerComponent | null}
   */
  #getProductCustomizer() {
    const section = this.closest('.shopify-section, featured-product-information');
    let customizer = section?.querySelector('product-customizer-component');

    if (!customizer && this.dataset.sectionId) {
      customizer = document.querySelector(
        `product-customizer-component[data-section-id="${this.dataset.sectionId}"]`
      );
    }

    if (customizer && 'requiresNativeSubmit' in customizer) {
      return /** @type {import('./product-customizer').ProductCustomizerComponent} */ (customizer);
    }

    return null;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId) {
      const validation = await this.checkQuantityLimitAsync();
      if (!validation.canAdd) {
        this.#handleQuantityLimitFailure(validation);
        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          this.#showAddToCartError(response.message);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          this.#hideAddToCartError();

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * @param {Array<{variantId: string, quantity: number}>} items
   * @param {Event} [event]
   * @returns {Promise<boolean>}
   */
  async #processBatchAddToCart(items, event) {
    if (items.length === 0) return false;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    allAddToCartContainers.forEach((container) => container.disable());

    const sectionIds = this.#getCartSectionIds();
    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: sectionIds.join(','),
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const parsedResponse = await response.json();

      if (parsedResponse.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id, parsedResponse.message, parsedResponse.description, parsedResponse.errors)
        );

        this.#showAddToCartError(parsedResponse.message);

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
            productId: this.dataset.productId,
          })
        );
        return false;
      }

      this.#hideAddToCartError();

      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
        this.#setLiveRegionText(addedText);
        setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      const cart = await this.#fetchAndUpdateCartQuantity();
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id, {
          source: 'product-form-component',
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          sections: parsedResponse.sections,
        })
      );

      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      allAddToCartContainers.forEach((container) => container.enable());
      if (event) {
        cartPerformance.measureFromEvent('add:user-action', event);
      }
    }
  }

  /**
   * @param {import('./product-customizer').ProductCustomizerComponent} customizer
   * @param {Array<{variantId: string, quantity: number, colorSlug: string, featuredImageUrl: string}>} items
   * @param {Event} [event]
   * @returns {Promise<boolean>}
   */
  async #processBulkCustomizerAddToCart(customizer, items, event) {
    if (!items.length) return false;
    if (!customizer.validate()) return false;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return false;

    const validation = await this.checkQuantityLimitAsync();
    if (!validation.canAdd) {
      this.#handleQuantityLimitFailure(validation);
      return false;
    }

    allAddToCartContainers.forEach((container) => {
      container.disable();
    });
    document.dispatchEvent(new CustomizerCartPendingStartEvent());

    try {
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      const customizerKey = customizer.getCustomizerKey();
      const willAddAddons = customizer.hasAddOnVariants() && customizer.getEnabledLocations().length > 0;
      const sectionIds = this.#getCartSectionIds();
      const sectionsParam = sectionIds.join(',');

      customizer.setCustomizerKeyOnForm(customizerKey);
      customizer.setCustomizerBundleOnForm(willAddAddons);

      await customizer.prepareArtworkFilesForCart();

      /** @type {Map<string, Array<{variantId: string, quantity: number, colorSlug: string, featuredImageUrl: string}>>} */
      const colorGroups = new Map();
      for (const item of items) {
        const groupKey = item.colorSlug || item.variantId;
        if (!colorGroups.has(groupKey)) {
          colorGroups.set(groupKey, []);
        }
        colorGroups.get(groupKey)?.push(item);
      }

      let finalSections;
      const colorGroupEntries = Array.from(colorGroups.entries());
      /** @type {Record<string, string> | null} */
      let sharedArtworkUrls = null;

      for (let groupIndex = 0; groupIndex < colorGroupEntries.length; groupIndex++) {
        const [colorSlug, groupItems] = colorGroupEntries[groupIndex];
        const isLastColorGroup = groupIndex === colorGroupEntries.length - 1;
        const representative = groupItems[0];

        await customizer.preparePreviewsForBulkLine({
          colorSlug,
          variantId: representative.variantId,
          featuredImageUrl: representative.featuredImageUrl,
          skipPreviewFiles: false,
        });

        const missingPreviews = await customizer.ensurePreviewFilesReady();
        if (missingPreviews.length) {
          const labels = missingPreviews
            .map((location) => location.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()))
            .join(', ');
          alert(`Could not generate the design preview for ${labels}. Please try again before adding to cart.`);
          return false;
        }

        const firstItem = groupItems[0];
        const remainingItems = groupItems.slice(1);
        const isOnlyGarmentAdd = isLastColorGroup && remainingItems.length === 0 && !willAddAddons;

        const formData = new FormData(form);
        customizer.appendPropertyFilesToFormData(formData, {
          includePreviews: true,
          includeArtwork: !sharedArtworkUrls,
        });
        if (sharedArtworkUrls) {
          for (const [key, value] of Object.entries(sharedArtworkUrls)) {
            formData.append(`properties[${key}]`, value);
          }
        }
        formData.set('id', firstItem.variantId);
        formData.set('quantity', firstItem.quantity.toString());
        if (isOnlyGarmentAdd && sectionsParam) {
          formData.append('sections', sectionsParam);
        }

        const parsed = await this.#fetchCartAddJson(formData);

        if (parsed.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              parsed.message,
              parsed.description,
              parsed.errors
            )
          );

          this.#showAddToCartError(parsed.message);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: totalQuantity,
              productId: this.dataset.productId,
            })
          );
          return false;
        }

        finalSections = parsed.sections || finalSections;

        const hasMoreGarmentLines = remainingItems.length > 0 || !isLastColorGroup;
        if (hasMoreGarmentLines) {
          const cart = await this.#fetchCartSnapshot();
          const anchorLine = this.#findCustomizerAnchorLine(cart, customizerKey, firstItem.variantId);

          if (!sharedArtworkUrls && anchorLine?.properties) {
            sharedArtworkUrls = this.#extractArtworkPropertyUrls(anchorLine.properties);
          }

          if (remainingItems.length > 0) {
            const sharedProperties = this.#copyLineItemProperties(anchorLine?.properties);
            const isLastGarmentBatch = isLastColorGroup && !willAddAddons;

            const batchResponse = await fetch(Theme.routes.cart_add_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({
                items: remainingItems.map((item) => ({
                  id: Number(item.variantId),
                  quantity: item.quantity,
                  properties: sharedProperties,
                })),
                ...(isLastGarmentBatch && sectionsParam ? { sections: sectionsParam } : {}),
              }),
            });
            const batchParsed = await batchResponse.json();

            if (batchParsed.status) {
              this.dispatchEvent(
                new CartErrorEvent(
                  form.getAttribute('id') || '',
                  batchParsed.message,
                  batchParsed.description,
                  batchParsed.errors
                )
              );

              this.#showAddToCartError(batchParsed.message);

              this.dispatchEvent(
                new CartAddEvent({}, this.id, {
                  didError: true,
                  source: 'product-form-component',
                  itemCount: totalQuantity,
                  productId: this.dataset.productId,
                })
              );
              return false;
            }

            finalSections = batchParsed.sections || finalSections;
          }
        }
      }

      customizer.clearPreviewColorSlugOverride();

      if (willAddAddons) {
        const addonItems = customizer.getAddOnCartItems(totalQuantity, customizerKey);
        if (addonItems.length) {
          const addonResponse = await fetch(Theme.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: addonItems,
              ...(sectionsParam ? { sections: sectionsParam } : {}),
            }),
          });
          const addonParsed = await addonResponse.json();

          if (addonParsed.status) {
            this.dispatchEvent(
              new CartErrorEvent(this.id, addonParsed.message, addonParsed.description, addonParsed.errors)
            );
            this.#showAddToCartError(addonParsed.message);
            return false;
          }

          finalSections = addonParsed.sections || finalSections;
        }

        this.#redirectToCartPage();
        return true;
      }

      this.#hideAddToCartError();

      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
        this.#setLiveRegionText(addedText);
        setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      allAddToCartContainers.forEach((container) => {
        container.animateAddToCart?.();
      });

      const cart = await this.#fetchAndUpdateCartQuantity();
      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id, {
          source: 'product-form-component',
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          sections: finalSections,
        })
      );

      return true;
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Could not add your customised product to the cart. Please try again.';
      this.#showAddToCartError(message);
      alert(message);
      return false;
    } finally {
      customizer.clearPreviewColorSlugOverride();
      document.dispatchEvent(new CustomizerCartPendingEndEvent());
      allAddToCartContainers.forEach((container) => {
        container.enable();
      });

      if (event) {
        cartPerformance.measureFromEvent('add:user-action', event);
      }
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
